import { useState, useCallback, useEffect } from 'react';
import { useAppSelector } from './redux';
import { subscriptionAPI } from '../services/api';

// Subscription tiers have been removed — the platform is fully free.
// The only restriction is the CRM gallery image upload limit (1000 total).
export type RestrictionAction =
  | 'create_showcase_section'
  | 'create_portfolio_item'
  | 'create_collab_request'
  | 'create_collab_post'
  | 'access_crm'
  | 'create_crm_project'
  | 'create_crm_lead'
  | 'upload_gallery_image';

interface UsageData {
  showcase_sections: number;
  portfolio_items: number;
  collab_requests_month: number;
  collab_posts: number;
  active_projects: number;
  leads_month: number;
  gallery_images: number;
}

const GALLERY_IMAGE_LIMIT = 1000;

export const useRestrictions = () => {
  const { user } = useAppSelector((state) => state.auth);
  const [usage, setUsage] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUsage = useCallback(async () => {
    try {
      setLoading(true);
      const response = await subscriptionAPI.getUserUsage();
      if (response.data.success) {
        setUsage(response.data.data.usage);
      }
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchUsage();
    }
  }, [user, fetchUsage]);

  const checkRestriction = (action: RestrictionAction, count: number = 1): {
    allowed: boolean;
    reason?: string;
    featureName?: string;
    limit?: string;
  } => {
    // All actions are unrestricted except uploading CRM gallery images (max 1000)
    if (action === 'upload_gallery_image') {
      if (usage !== null && usage.gallery_images + count > GALLERY_IMAGE_LIMIT) {
        return {
          allowed: false,
          featureName: 'Gallery Images',
          limit: `${GALLERY_IMAGE_LIMIT}`,
          reason: `You have reached the maximum of ${GALLERY_IMAGE_LIMIT} CRM gallery images. Please delete some images to upload more.`,
        };
      }
    }

    // All other actions are unrestricted — platform is fully free.
    return { allowed: true };
  };

  return {
    usage,
    // Expose a simplified limits object for compatibility with CRM usage counter
    limits: usage !== null ? {
      is_crm_enabled: true,
      max_gallery_images: GALLERY_IMAGE_LIMIT,
      max_showcase_sections: -1,
      max_images_per_section: -1,
      max_portfolio_items: -1,
      max_collab_requests_per_month: -1,
      max_collab_posts: -1,
      max_images_per_collab_post: 15,
      max_active_projects: -1,
      max_leads_per_month: -1,
    } : null,
    loading,
    checkRestriction,
    refreshUsage: fetchUsage,
  };
};
